import type { ReactNode } from 'react'
import { cn } from '../../utils/cn'

/**
 * Badge — semantic chips.
 *
 *  <Badge variant="tier" value="LATE" />       timing tier (Critical/Late/At risk/On time/Ahead/Knock-on)
 *  <Badge variant="status" value="IN_TRANSIT" /> load / generic entity status
 *  <Badge variant="hos" value="AMBER" />       HOS traffic light
 *  <Badge tone="success">Active</Badge>       free-form tone
 */

export type TierValue = 'CRITICAL' | 'LATE' | 'AT_RISK' | 'ON_TIME' | 'AHEAD' | 'KNOCK_ON'
export type HosValue = 'GREEN' | 'AMBER' | 'RED'
export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'brand' | 'ai'

const TONE: Record<Tone, string> = {
  neutral: 'bg-surface-sunken text-fg-muted border-line',
  info: 'bg-blue-50 text-status-info border-blue-200',
  success: 'bg-green-50 text-status-success border-green-200',
  warning: 'bg-amber-50 text-status-warning border-amber-200',
  danger: 'bg-red-50 text-status-danger border-red-200',
  brand: 'bg-brand-50 text-brand-700 border-brand-200',
  ai: 'bg-purple-50 text-purple-700 border-purple-200',
}

export const TIER_META: Record<TierValue, { label: string; className: string; dot: string }> = {
  CRITICAL: { label: 'Critical', className: 'bg-red-50 text-tier-critical border-red-200', dot: 'bg-tier-critical' },
  LATE: { label: 'Late', className: 'bg-orange-50 text-tier-late border-orange-200', dot: 'bg-tier-late' },
  AT_RISK: { label: 'At risk', className: 'bg-yellow-50 text-tier-at-risk border-yellow-200', dot: 'bg-tier-at-risk' },
  ON_TIME: { label: 'On time', className: 'bg-green-50 text-tier-on-time border-green-200', dot: 'bg-tier-on-time' },
  AHEAD: { label: 'Ahead', className: 'bg-sky-50 text-tier-ahead border-sky-200', dot: 'bg-tier-ahead' },
  KNOCK_ON: { label: 'Knock-on', className: 'bg-purple-50 text-tier-knock-on border-purple-200', dot: 'bg-tier-knock-on' },
}

const HOS_META: Record<HosValue, { label: string; className: string; dot: string }> = {
  GREEN: { label: 'HOS OK', className: 'bg-green-50 text-hos-green border-green-200', dot: 'bg-hos-green' },
  AMBER: { label: 'HOS warning', className: 'bg-amber-50 text-hos-amber border-amber-200', dot: 'bg-hos-amber' },
  RED: { label: 'HOS breach', className: 'bg-red-50 text-hos-red border-red-200', dot: 'bg-hos-red' },
}

/** Shared status → tone map (loads, users, devices, drivers). */
export function statusTone(status: string | null | undefined): Tone {
  const s = (status ?? '').toUpperCase()
  if (['COMPLETED', 'DELIVERED', 'ACTIVE', 'ACCEPTED', 'DONE', 'RESOLVED', 'ONLINE', 'SUCCESS'].includes(s)) return 'success'
  if (['IN_TRANSIT', 'IN_PROGRESS', 'EN_ROUTE', 'ASSIGNED', 'DISPATCHED', 'PROPOSED', 'RUNNING'].includes(s)) return 'info'
  if (['PLANNED', 'PENDING', 'AWAITING', 'SCHEDULED', 'PROPOSED_PLAN', 'SNOOZED', 'ON_LEAVE'].includes(s)) return 'warning'
  if (['CANCELLED', 'CANCELED', 'FAILED', 'INACTIVE', 'DECLINED', 'TERMINATED', 'ERROR', 'OFFLINE', 'UNCOVERED', 'LOST'].includes(s)) return 'danger'
  return 'neutral'
}

export function humanize(value: string | null | undefined): string {
  if (!value) return '—'
  return value.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase())
}

interface BaseProps {
  className?: string
  size?: 'sm' | 'md'
  /** Show a leading coloured dot (useful when colour alone must not carry meaning) */
  dot?: boolean
  title?: string
}

type BadgeProps =
  | (BaseProps & { variant: 'tier'; value: TierValue | string | null | undefined; children?: never; tone?: never })
  | (BaseProps & { variant: 'hos'; value: HosValue | string | null | undefined; children?: never; tone?: never })
  | (BaseProps & { variant: 'status'; value: string | null | undefined; children?: ReactNode; tone?: never })
  | (BaseProps & { variant?: undefined; tone?: Tone; children: ReactNode; value?: never })

export function Badge(props: BadgeProps) {
  const { className, size = 'sm', dot, title } = props
  const base = cn(
    'inline-flex items-center gap-1 rounded-full border font-medium whitespace-nowrap',
    size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
  )

  if (props.variant === 'tier') {
    const key = (props.value ?? '').toUpperCase() as TierValue
    const meta = TIER_META[key]
    if (!meta) return <span className={cn(base, TONE.neutral, className)} title={title}>{humanize(props.value)}</span>
    return (
      <span className={cn(base, meta.className, className)} title={title}>
        {dot !== false && <span className={cn('w-1.5 h-1.5 rounded-full', meta.dot)} aria-hidden />}
        {meta.label}
      </span>
    )
  }
  if (props.variant === 'hos') {
    const key = (props.value ?? '').toUpperCase() as HosValue
    const meta = HOS_META[key]
    if (!meta) return <span className={cn(base, TONE.neutral, className)} title={title}>{humanize(props.value)}</span>
    return (
      <span className={cn(base, meta.className, className)} title={title}>
        <span className={cn('w-2 h-2 rounded-full', meta.dot)} aria-hidden />
        {meta.label}
      </span>
    )
  }
  if (props.variant === 'status') {
    const tone = statusTone(props.value)
    return (
      <span className={cn(base, TONE[tone], className)} title={title}>
        {dot && <span className={cn('w-1.5 h-1.5 rounded-full bg-current')} aria-hidden />}
        {props.children ?? humanize(props.value)}
      </span>
    )
  }
  return (
    <span className={cn(base, TONE[props.tone ?? 'neutral'], className)} title={title}>
      {dot && <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden />}
      {props.children}
    </span>
  )
}

export default Badge
